import { useState } from 'react'
import { Pagination } from '@/components/common'
import { usePrompts } from '@/hooks/usePrompts'
import type { Prompt } from '@/types'

interface PromptListProps {
  onPromptSelect: (id: string) => void
  onCreateNew: () => void
}

const PAGE_SIZE = 50

export function PromptList({ onPromptSelect, onCreateNew }: PromptListProps) {
  const [page, setPage] = useState(1)
  const [useCaseFilter, setUseCaseFilter] = useState<string>('')

  const { data, isLoading, error, refetch } = usePrompts({
    use_case: useCaseFilter || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="error-state">
        <p className="text-red-800">Failed to load prompts</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="prompt-list">
      {/* Header with filter and create button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label htmlFor="use-case-filter" className="text-sm font-medium text-gray-700">
            Filter by Use Case:
          </label>
          <select
            id="use-case-filter"
            value={useCaseFilter}
            onChange={(e) => {
              setUseCaseFilter(e.target.value)
              setPage(1)
            }}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            data-testid="use-case-filter"
          >
            <option value="">All Use Cases</option>
            <option value="extraction">extraction</option>
            <option value="classification">classification</option>
            <option value="summarization">summarization</option>
          </select>
        </div>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          data-testid="create-new-btn"
        >
          Create New Prompt
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8" data-testid="loading-state">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !data?.prompts?.length ? (
        <div className="text-center py-8 text-gray-500" data-testid="empty-state">
          No prompts found
        </div>
      ) : (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Use Case
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model ID
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    {/* Action column */}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.prompts.map((prompt: Prompt) => (
                  <tr
                    key={prompt.id}
                    onClick={() => onPromptSelect(prompt.id)}
                    className={`cursor-pointer hover:bg-gray-50 ${
                      prompt.is_active ? 'bg-green-50' : ''
                    }`}
                    data-testid={`prompt-row-${prompt.id}`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {prompt.name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prompt.use_case}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prompt.model_id}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      {prompt.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              className="mt-4"
            />
          )}

          <div className="text-sm text-gray-500 text-center">
            Showing {data.prompts.length} of {data.total} prompts
          </div>
        </>
      )}
    </div>
  )
}
